/**
 * Capture what the user is looking at, for a reported Issue (browser only).
 *
 * Snapshots `document.body` — not the React root — so open drawers, popovers and other
 * portalled panels are in the picture, at the viewport's size and scroll positions.
 * Excluded: anything marked `data-issue-capture-ignore` (the life ring, the report form)
 * and toasts. The caller hides the report form before calling this.
 *
 * Images that can't be fetched (another site without CORS) render blank rather than
 * failing the capture; if the capture itself fails, the form offers "Upload an image".
 */

import { domToBlob } from "modern-screenshot";

const IGNORE_ATTR = "data-issue-capture-ignore";

/** Above this pixel ratio a screenshot gets large for little gain. */
const MAX_SCALE = 2;

function keep(node: Node): boolean {
  if (!(node instanceof Element)) return true;
  if (node.hasAttribute(IGNORE_ATTR)) return false;
  if (node.hasAttribute("data-sonner-toaster")) return false;
  // A dialog or its backdrop still animating out (the report form's own) was being
  // closed, not looked at.
  const slot = node.getAttribute("data-slot");
  const closingDialog =
    node.getAttribute("data-state") === "closed" &&
    (slot === "dialog-overlay" || slot === "dialog-content");
  return !closingDialog;
}

export interface CapturedImage {
  blob: Blob;
  width: number;
  height: number;
}

/** Wait for the next paint — twice, so a just-closed dialog has left the screen. */
export function nextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

/**
 * Wait until every running animation (a closing dialog's fade) has finished, or `maxMs`
 * has passed — so the capture shows the page, not the report form fading away.
 */
export async function settleAnimations(maxMs = 1000): Promise<void> {
  const running = document.getAnimations().map((a) => a.finished.catch(() => undefined));
  await Promise.race([Promise.all(running), new Promise((resolve) => setTimeout(resolve, maxMs))]);
  await nextPaint();
}

export async function captureScreen(): Promise<CapturedImage> {
  const scale = Math.min(MAX_SCALE, window.devicePixelRatio || 1);
  const width = window.innerWidth;
  const height = window.innerHeight;
  const background = getComputedStyle(document.body).backgroundColor || null;
  const blob = await domToBlob(document.body, {
    width,
    height,
    scale,
    type: "image/png",
    backgroundColor: background,
    // The clone otherwise picks up the browser's default 8px body margin and comes out
    // shifted — visible now that markup lays the capture exactly over the page.
    style: { margin: "0" },
    filter: keep,
    timeout: 10_000,
  });
  return { blob, width: Math.round(width * scale), height: Math.round(height * scale) };
}

/** Attribute that keeps an element out of every capture. */
export const CAPTURE_IGNORE_PROPS = { [IGNORE_ATTR]: "" } as const;
